import moment from 'moment'
import { assetPrefix } from 'config/env_config'
import { PURCHASED_PACKAGE_SCHEDULING_STEPS } from 'src/utils/schedulingConstants'
import { getServiceIconByName, splitServiceName, getNextServicePositionInPackage } from 'utils'

const PURCHASED_PACKAGE_FIELD_CONFIGS = {
  service: {
    stepName: 'Serviço',
    step: PURCHASED_PACKAGE_SCHEDULING_STEPS.PET_AND_SERVICE,
    infoIcon: (serviceName) => getServiceIconByName(serviceName, 'outline'),
    infoTitle: (serviceName) => splitServiceName(serviceName),
    infoAdditional: (selectedPurchasedPackage) => {
      const packageAvailable = selectedPurchasedPackage?.service?.packageQuantity - selectedPurchasedPackage?.currentUsages?.service || 0
      return `${packageAvailable} ${packageAvailable === 1 ? 'disponível' : 'disponíveis'}`
    }
  },
  additionalService: {
    stepName: 'Serviço adicional',
    step: PURCHASED_PACKAGE_SCHEDULING_STEPS.PET_AND_SERVICE,
    infoIcon: (serviceName) => getServiceIconByName(serviceName),
    infoTitle: (serviceName) => splitServiceName(serviceName),
    infoAdditional: 'Serviço adicional'
  },
  partner: {
    stepName: 'Localização',
    step: PURCHASED_PACKAGE_SCHEDULING_STEPS.DATE_AND_TIME,
    infoIcon: () => `${assetPrefix}/static/icons/location-disabled.svg`
  },
  professional: {
    stepName: 'Profissional',
    step: PURCHASED_PACKAGE_SCHEDULING_STEPS.DATE_AND_TIME,
    infoIcon: () => `${assetPrefix}/static/icons/professional-disabled.svg`
  },
  datetime: {
    stepName: 'Data e Hora',
    step: PURCHASED_PACKAGE_SCHEDULING_STEPS.DATE_AND_TIME,
    infoIcon: () => `${assetPrefix}/static/icons/datetime-disabled.svg`
  },
  observations: {
    stepName: 'Serviço',
    step: PURCHASED_PACKAGE_SCHEDULING_STEPS.PET_AND_SERVICE,
    infoIcon: () => `${assetPrefix}/static/icons/comment.svg`
  }
}

const NON_EDITABLE_FIELDS = ['service', 'partner', 'professional']

const isFieldEditable = (fieldType) => {
  return !NON_EDITABLE_FIELDS.includes(fieldType)
}

export const getPurchasedPackageSchedulingInfoConfig = ({
  selectedService,
  selectedAdditionalServices,
  selectedPartner,
  selectedProfessional,
  selectedDay,
  selectedTime,
  obs,
  invertStep,
  selectedPurchasedPackage,
  hasSelectedPartner,
  serviceIndex
}, isEditing) => {
  const configs = []

  if (selectedService) {
    configs.push({
      infoTitle: PURCHASED_PACKAGE_FIELD_CONFIGS.service.infoTitle(selectedPurchasedPackage?.service?.name || selectedService.name),
      infoIcon: PURCHASED_PACKAGE_FIELD_CONFIGS.service.infoIcon(selectedService.name),
      infoAdditional: PURCHASED_PACKAGE_FIELD_CONFIGS.service.infoAdditional(selectedPurchasedPackage),
      stepName: PURCHASED_PACKAGE_FIELD_CONFIGS.service.stepName,
      step: PURCHASED_PACKAGE_FIELD_CONFIGS.service.step,
      noEdit: !isFieldEditable('service'),
      fieldType: 'service'
    })
  }

  if (selectedAdditionalServices?.length > 0) {
    selectedAdditionalServices.forEach(additionalService => {
      configs.push({
        infoTitle: PURCHASED_PACKAGE_FIELD_CONFIGS.additionalService.infoTitle(additionalService.name),
        infoIcon: PURCHASED_PACKAGE_FIELD_CONFIGS.additionalService.infoIcon(additionalService.name),
        infoAdditional: PURCHASED_PACKAGE_FIELD_CONFIGS.additionalService.infoAdditional,
        stepName: PURCHASED_PACKAGE_FIELD_CONFIGS.additionalService.stepName,
        step: PURCHASED_PACKAGE_FIELD_CONFIGS.additionalService.step,
        noEdit: !isFieldEditable('additionalService'),
        fieldType: 'additionalService'
      })
    })
  }

  if (selectedPartner) {
    configs.push({
      infoTitle: hasSelectedPartner ? selectedPartner.name : selectedPartner.fantasyName,
      infoIcon: PURCHASED_PACKAGE_FIELD_CONFIGS.partner.infoIcon(),
      infoAdditional: selectedPartner.address,
      stepName: PURCHASED_PACKAGE_FIELD_CONFIGS.partner.stepName,
      step: PURCHASED_PACKAGE_FIELD_CONFIGS.partner.step,
      noEdit: !isFieldEditable('partner'),
      fieldType: 'partner'
    })
  }

  if (selectedProfessional) {
    configs.push({
      infoTitle: selectedProfessional.name?.split('CNPJ')?.[0],
      infoIcon: PURCHASED_PACKAGE_FIELD_CONFIGS.professional.infoIcon(),
      stepName: PURCHASED_PACKAGE_FIELD_CONFIGS.professional.stepName,
      step: typeof PURCHASED_PACKAGE_FIELD_CONFIGS.professional.step === 'function'
        ? PURCHASED_PACKAGE_FIELD_CONFIGS.professional.step(invertStep)
        : PURCHASED_PACKAGE_FIELD_CONFIGS.professional.step,
      noEdit: !isFieldEditable('professional'),
      fieldType: 'professional'
    })
  }

  if (selectedDay) {
    const serviceName = selectedPurchasedPackage?.service?.linkedService?.name || selectedService?.name
    const bathIdentification = isEditing
      ? serviceIndex
      : getNextServicePositionInPackage(selectedPurchasedPackage)
    const formattedDate = moment(selectedDay).format('DD/MM/YYYY')

    configs.push({
      infoTitle: bathIdentification ? `${serviceName} ${bathIdentification}` : serviceName,
      infoIcon: PURCHASED_PACKAGE_FIELD_CONFIGS.datetime.infoIcon(),
      infoAdditional: selectedTime ? `${formattedDate} - ${selectedTime}` : formattedDate,
      stepName: PURCHASED_PACKAGE_FIELD_CONFIGS.datetime.stepName,
      step: typeof PURCHASED_PACKAGE_FIELD_CONFIGS.datetime.step === 'function'
        ? PURCHASED_PACKAGE_FIELD_CONFIGS.datetime.step(invertStep)
        : PURCHASED_PACKAGE_FIELD_CONFIGS.datetime.step,
      noEdit: !isFieldEditable('datetime'),
      fieldType: 'datetime'
    })
  }

  if (obs) {
    configs.push({
      infoTitle: 'Observações',
      infoAdditional: obs,
      infoIcon: PURCHASED_PACKAGE_FIELD_CONFIGS.observations.infoIcon(),
      stepName: PURCHASED_PACKAGE_FIELD_CONFIGS.observations.stepName,
      step: PURCHASED_PACKAGE_FIELD_CONFIGS.observations.step,
      noEdit: !isFieldEditable('observations'),
      fieldType: 'observations'
    })
  }

  return configs
}
